import * as React from 'react';
import { useMemo } from 'react';
import type { FormikProps } from 'formik';
import { useFormikContext } from 'formik';

import type { ReceiptBatchFormValues } from './ServiceProviderReceiptBatchForm';
import { ServiceProviderReceiptBatchCommonView } from './ServiceProviderReceiptBatchCommonView';
import { useReceiptBatchStore } from '../ReceiptBatchContext';
import { useShallow } from 'zustand/react/shallow';

interface ReceiptBatchCommonProps<T extends ReceiptBatchFormValues> {
  renderReceiptBatchCommonForm: (formik: FormikProps<T>) => React.ReactElement;
  renderReceiptBatchImageInput: (formik: FormikProps<T>) => React.ReactElement;
  renderCashLink?: (formik: FormikProps<T>) => React.ReactElement;
}

export function ServiceProviderReceiptBatchCommonContainer<
  T extends ReceiptBatchFormValues
>({
  renderReceiptBatchCommonForm,
  renderReceiptBatchImageInput,
}: ReceiptBatchCommonProps<T>): React.ReactElement {
  const formik = useFormikContext<T>();

  const [totalAmount, receiptsCount] = useReceiptBatchStore(
    useShallow((state) => [
      state?.formik?.values.receipts
        .flatMap((receipt) => receipt.lineItems ?? [])
        .reduce((acc, lineItem) => acc + (lineItem.amount ?? 0), 0) ?? 0,
      state?.formik?.values.receipts.length ?? 0,
    ])
  );

  const ReceiptBatchCommonForm = useMemo(() => {
    return renderReceiptBatchCommonForm(formik);
  }, [renderReceiptBatchCommonForm, formik]);

  const ReceiptBatchImageInput = useMemo(() => {
    return renderReceiptBatchImageInput(formik);
  }, [renderReceiptBatchImageInput, formik]);

  return (
    <ServiceProviderReceiptBatchCommonView
      receiptsCount={receiptsCount}
      totalAmount={totalAmount}
      ReceiptBatchCommonForm={ReceiptBatchCommonForm}
      ReceiptBatchImageInput={ReceiptBatchImageInput}
      autoGenerateReceipts={formik.values.generateReceipts}
    />
  );
}
